import { useState, useCallback, useRef } from 'react';
import type { CardQuestion, SurveyPath, CardSurvey, SurveyQuestionType } from '../types';
import { DONE_QUESTION_ID, DONE_STEP_JSON_ID } from '../types';
import { normalizeTriggerValues } from '../utils/pathTriggerValues';

const MAX_HISTORY = 50;

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export interface UseCardBuilderResult {
  questions: CardQuestion[];
  paths: SurveyPath[];
  addQuestion: (type: SurveyQuestionType, afterId?: string) => CardQuestion;
  addQuestionToPath: (type: SurveyQuestionType, pathId: string) => CardQuestion;
  duplicateQuestion: (questionId: string) => CardQuestion | undefined;
  updateQuestion: (id: string, updates: Partial<CardQuestion>) => void;
  deleteQuestion: (id: string) => void;
  reorderQuestion: (id: string, direction: 'up' | 'down') => void;
  reorderMainQuestionBlock: (mainQuestionId: string, direction: 'up' | 'down') => void;
  moveMainQuestionBlockToIndex: (mainQuestionId: string, targetMainIndex: number) => void;
  moveQuestionToPath: (questionId: string, pathId: string) => void;
  moveQuestionOutOfPath: (questionId: string, targetMainIndex: number) => void;
  reorderQuestionInPath: (questionId: string, direction: 'up' | 'down') => void;
  moveQuestionWithinPath: (questionId: string, targetPathIndex: number) => void;
  moveQuestionToPathAt: (questionId: string, pathId: string, targetPathIndex: number) => void;
  addPath: (path: Omit<SurveyPath, 'id'>) => string;
  updatePath: (id: string, updates: Partial<SurveyPath>) => void;
  deletePath: (id: string) => void;
  getCardSurvey: () => CardSurvey;
  loadCardSurvey: (survey: CardSurvey) => void;
  getQuestionById: (id: string) => CardQuestion | undefined;
  getPathById: (id: string) => SurveyPath | undefined;
  getQuestionsByPath: (pathId: string) => CardQuestion[];
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * Hook for managing card-based survey builder state
 * Provides all CRUD operations for questions and paths
 */
type HistorySnapshot = { questions: CardQuestion[]; paths: SurveyPath[] };
type HistoryState = { history: HistorySnapshot[]; index: number };

/** Ensure path has trigger and questionIds so we never crash on undefined. */
function normalizePath(p: Partial<SurveyPath> & { id: string }): SurveyPath {
  const trigger = (p.trigger ?? { values: undefined, questionId: undefined, label: undefined }) as {
    values?: unknown;
    questionId?: string;
    label?: string;
  };
  const values = normalizeTriggerValues(trigger.values);
  const questionIds = Array.isArray(p.questionIds) ? p.questionIds : [];
  const name =
    p.name != null && String(p.name).trim() !== ''
      ? String(p.name).trim()
      : values.length > 0
        ? `${values.join(', ')} Path`
        : 'Path';
  return {
    id: p.id,
    name,
    trigger: {
      questionId: trigger.questionId ?? '',
      values,
      label: trigger.label ?? values[0],
    },
    questionIds,
    afterPath: p.afterPath ?? 'continue',
  };
}

function copySnapshot(s: HistorySnapshot): HistorySnapshot {
  return {
    questions: s.questions.map((q) => ({ ...q, config: { ...q.config } })),
    paths: s.paths.map((p) => normalizePath(p)),
  };
}

/** Expand minimal "done" from API ({ id?, number, type }) to full CardQuestion for editor. */
function hydrateQuestions(questions: CardSurvey['questions']): CardQuestion[] {
  const list = Array.isArray(questions) ? questions : [];
  return list.map((q) => {
    const isMinimalDone =
      q.type === 'done' &&
      ((q as CardQuestion).id == null || (q as CardQuestion).id === DONE_STEP_JSON_ID);
    if (isMinimalDone) {
      const num = typeof (q as { number?: number }).number === 'number' ? (q as { number: number }).number : 1;
      return {
        id: DONE_QUESTION_ID,
        number: num,
        text: '',
        type: 'done',
        required: false,
        config: {},
      } as CardQuestion;
    }
    return q as CardQuestion;
  });
}

export function useCardBuilder(initialSurvey?: CardSurvey): UseCardBuilderResult {
  const initialSnapshot: HistorySnapshot = {
    questions: initialSurvey ? hydrateQuestions(initialSurvey.questions) : [],
    paths: (initialSurvey?.paths || []).map((p) => normalizePath(p as Partial<SurveyPath> & { id: string })),
  };

  const [questions, setQuestions] = useState<CardQuestion[]>(initialSnapshot.questions);
  const [paths, setPaths] = useState<SurveyPath[]>(initialSnapshot.paths);

  const [historyState, setHistoryState] = useState<HistoryState>(() => ({
    history: initialSnapshot.questions.length > 0 || initialSnapshot.paths.length > 0 ? [copySnapshot(initialSnapshot)] : [],
    index: 0,
  }));

  const isRestoringRef = useRef(false);

  const pushHistory = useCallback((snapshot: HistorySnapshot) => {
    if (isRestoringRef.current) return;
    const copied = copySnapshot(snapshot);
    setHistoryState((prev) => {
      const next = [...prev.history.slice(0, prev.index + 1), copied].slice(-MAX_HISTORY);
      return { history: next, index: next.length - 1 };
    });
  }, []);

  const undo = useCallback(() => {
    if (isRestoringRef.current) return;
    setHistoryState((prev) => {
      if (prev.index <= 0) return prev;
      const newIndex = prev.index - 1;
      const snapshot = prev.history[newIndex];
      if (snapshot) {
        isRestoringRef.current = true;
        setQuestions(snapshot.questions);
        setPaths(snapshot.paths);
        queueMicrotask(() => {
          isRestoringRef.current = false;
        });
      }
      return { ...prev, index: newIndex };
    });
  }, []);

  const redo = useCallback(() => {
    if (isRestoringRef.current) return;
    setHistoryState((prev) => {
      if (prev.index >= prev.history.length - 1) return prev;
      const newIndex = prev.index + 1;
      const snapshot = prev.history[newIndex];
      if (snapshot) {
        isRestoringRef.current = true;
        setQuestions(snapshot.questions);
        setPaths(snapshot.paths);
        queueMicrotask(() => {
          isRestoringRef.current = false;
        });
      }
      return { ...prev, index: newIndex };
    });
  }, []);

  const canUndo = historyState.index > 0;
  const canRedo = historyState.index < historyState.history.length - 1;

  /**
   * Add a new question
   */
  const addQuestion = useCallback(
    (type: SurveyQuestionType, afterId?: string): CardQuestion => {
      const newId = generateId();
      const allQuestions = [...questions];

      // Find insertion index (never after the done step — survey end must stay last)
      let insertIndex = allQuestions.length;
      if (afterId) {
        const afterIndex = allQuestions.findIndex((q) => q.id === afterId);
        if (afterIndex !== -1) {
          insertIndex = afterIndex + 1;
        }
      }
      const doneIndex = allQuestions.findIndex(
        (q) => !q.pathId && (q.type === 'done' || q.id === DONE_STEP_JSON_ID)
      );
      if (doneIndex !== -1 && insertIndex > doneIndex) {
        insertIndex = doneIndex;
      }

      // Calculate question number
      let questionNumber: number | string;
      if (allQuestions.length === 0) {
        questionNumber = 1;
      } else {
        const lastQuestion = allQuestions[allQuestions.length - 1];
        const lastNum = typeof lastQuestion.number === 'number'
          ? lastQuestion.number
          : parseFloat(lastQuestion.number.toString());
        questionNumber = lastNum + 1;
      }

      const newQuestion: CardQuestion = {
        id: newId,
        number: questionNumber,
        text: type !== 'done' ? 'Question' : '',
        type,
        required: true,
        config: {
          format: type === 'rating' ? 'numerical' : undefined,
          rateAmount: type === 'rating' ? 5 : undefined,
          options:
            type === 'radio' || type === 'checkbox' ? ['Option 1'] : undefined,
        },
      };

      const updatedQuestions = [
        ...allQuestions.slice(0, insertIndex),
        newQuestion,
        ...allQuestions.slice(insertIndex),
      ];

      const renumbered = renumberQuestions(updatedQuestions, paths);
      pushHistory({ questions: renumbered, paths });
      setQuestions(renumbered);

      const finalQuestion = renumbered.find((q) => q.id === newId);
      if (!finalQuestion) {
        throw new Error('Failed to create question');
      }
      return finalQuestion;
    },
    [questions, paths, pushHistory]
  );

  /**
   * Add a new question directly into a path (atomic: question + path update in one batch).
   */
  const addQuestionToPath = useCallback(
    (type: SurveyQuestionType, pathId: string): CardQuestion => {
      const path = paths.find((p) => p.id === pathId);
      if (!path) throw new Error(`Path not found: ${pathId}`);

      const newId = generateId();
      const allQuestions = [...questions];
      let questionNumber: number | string;
      if (allQuestions.length === 0) {
        questionNumber = 1;
      } else {
        const lastQuestion = allQuestions[allQuestions.length - 1];
        const lastNum = typeof lastQuestion.number === 'number'
          ? lastQuestion.number
          : parseFloat(lastQuestion.number.toString());
        questionNumber = lastNum + 1;
      }

      const newQuestion: CardQuestion = {
        id: newId,
        number: questionNumber,
        text: type !== 'done' ? 'Question' : '',
        type,
        required: true,
        pathId,
        config: {
          format: type === 'rating' ? 'numerical' : undefined,
          rateAmount: type === 'rating' ? 5 : undefined,
          options:
            type === 'radio' || type === 'checkbox' ? ['Option 1'] : undefined,
        },
      };

      const doneIndex = allQuestions.findIndex(
        (q) => !q.pathId && (q.type === 'done' || q.id === DONE_STEP_JSON_ID)
      );
      const insertIndex = doneIndex !== -1 ? doneIndex : allQuestions.length;
      const updatedQuestions = [
        ...allQuestions.slice(0, insertIndex),
        newQuestion,
        ...allQuestions.slice(insertIndex),
      ];
      const nextPaths = paths.map((p) =>
        p.id === pathId ? { ...p, questionIds: [...p.questionIds, newId] } : p
      );
      const renumbered = renumberQuestions(updatedQuestions, nextPaths);
      pushHistory({ questions: renumbered, paths: nextPaths });
      setQuestions(renumbered);
      setPaths(nextPaths);

      const finalQuestion = renumbered.find((q) => q.id === newId);
      if (!finalQuestion) throw new Error('Failed to create question');
      return finalQuestion;
    },
    [questions, paths, pushHistory]
  );

  /**
   * Duplicate a question (copy with new id, inserted after original)
   */
  const duplicateQuestion = useCallback(
    (questionId: string): CardQuestion | undefined => {
      const source = questions.find((q) => q.id === questionId);
      if (!source) return undefined;
      if (source.type === 'done' || source.id === DONE_STEP_JSON_ID) return undefined;

      const newId = generateId();
      const copy: CardQuestion = {
        ...source,
        id: newId,
        text: source.text ? `${source.text} (copy)` : '',
        config: {
          ...source.config,
          options: source.config.options ? [...source.config.options] : undefined,
        },
      };

      const sourceIndex = questions.findIndex((q) => q.id === questionId);
      let insertIndex = sourceIndex + 1;
      const doneIndex = questions.findIndex(
        (q) => !q.pathId && (q.type === 'done' || q.id === DONE_STEP_JSON_ID)
      );
      if (doneIndex !== -1 && insertIndex > doneIndex) {
        insertIndex = doneIndex;
      }

      const updatedQuestions = [
        ...questions.slice(0, insertIndex),
        copy,
        ...questions.slice(insertIndex),
      ];
      const renumbered = renumberQuestions(updatedQuestions, paths);

      let nextPaths = paths;
      if (source.pathId) {
        const path = paths.find((p) => p.id === source.pathId);
        if (path) {
          const pathQuestionIds = [...path.questionIds];
          const origPos = pathQuestionIds.indexOf(questionId);
          pathQuestionIds.splice(origPos + 1, 0, newId);
          nextPaths = paths.map((p) =>
            p.id === source.pathId ? { ...p, questionIds: pathQuestionIds } : p
          );
        }
      }

      pushHistory({ questions: renumbered, paths: nextPaths });
      setQuestions(renumbered);
      setPaths(nextPaths);

      return renumbered.find((q) => q.id === newId);
    },
    [questions, paths, pushHistory]
  );

  /**
   * Update a question.
   * When only option labels change (rename at same index), path trigger.values and trigger.label
   * are updated so paths stay linked to the renamed option.
   */
  const updateQuestion = useCallback(
    (id: string, updates: Partial<CardQuestion>) => {
      setQuestions((prev) => {
        const next = prev.map((q) => (q.id === id ? { ...q, ...updates } : q));
        const oldQuestion = prev.find((q) => q.id === id);
        const newOptions = updates.config?.options;
        const oldOptions = oldQuestion?.config?.options;

        // Renames: oldValue -> newValue (same index, new value non-empty)
        const oldToNew = new Map<string, string>();
        // Removed: option values that were removed from the list (splice), not just cleared in-place
        const removedValues = new Set<string>();
        if (Array.isArray(newOptions) && Array.isArray(oldOptions)) {
          if (newOptions.length === oldOptions.length) {
            const newValuesSet = new Set(
              newOptions.map((o) => (o != null ? String(o).trim() : ''))
            );
            for (let i = 0; i < oldOptions.length; i++) {
              const o = oldOptions[i];
              const n = newOptions[i];
              const oTrim = o != null ? String(o).trim() : '';
              const nTrim = n != null ? String(n).trim() : '';
              if (oTrim !== '' && nTrim !== '' && oTrim !== nTrim) {
                oldToNew.set(oTrim, nTrim);
              }
            }
            // Option was cleared, now user typed again: path may have an "orphaned" trigger value
            // (not in newOptions). Link it to the new value so the path stays on this option.
            const filledInValues = newOptions
              .map((n, i) => (oldOptions[i] != null ? String(oldOptions[i]).trim() : '') === '' && (n != null ? String(n).trim() : '') !== '' ? String(n).trim() : null)
              .filter((v): v is string => v != null);
            const orphanedPathValues = paths
              .filter((p) => p.trigger.questionId === id)
              .flatMap((p) => normalizeTriggerValues(p.trigger.values))
              .filter((v) => v !== '' && !newValuesSet.has(v));
            if (filledInValues.length === 1 && orphanedPathValues.length === 1) {
              oldToNew.set(orphanedPathValues[0], filledInValues[0]);
            }
          } else if (newOptions.length < oldOptions.length) {
            // One or more options were removed from the list; remove those values from path triggers
            const countTrimmed = (arr: (string | undefined)[]) => {
              const m = new Map<string, number>();
              for (const v of arr) {
                const t = v != null ? String(v).trim() : '';
                if (t) m.set(t, (m.get(t) ?? 0) + 1);
              }
              return m;
            };
            const oldCounts = countTrimmed(oldOptions);
            const newCounts = countTrimmed(newOptions);
            for (const [v, c] of oldCounts) {
              if ((newCounts.get(v) ?? 0) < c) removedValues.add(v);
            }
          }
        }

        const hasPathChanges = oldToNew.size > 0 || removedValues.size > 0;
        let nextPaths = paths;
        if (hasPathChanges) {
          nextPaths = paths
            .map((p) => {
              if (p.trigger.questionId !== id) return p;
              const vals = normalizeTriggerValues(p.trigger.values);
              // Remove trigger values only when options were removed from the list
              let updatedVals = removedValues.size > 0
                ? vals.filter((v) => !removedValues.has(v))
                : vals;
              // Apply renames (never replace with empty)
              if (oldToNew.size > 0) {
                updatedVals = updatedVals.map((v) => oldToNew.get(v) ?? v);
              }
              if (updatedVals.length === 0) return null;
              if (
                updatedVals.length === vals.length &&
                updatedVals.every((v, i) => v === vals[i])
              ) {
                return p;
              }
              const newTrigger = {
                ...p.trigger,
                values: updatedVals,
                label: updatedVals.join(', '),
              };
              const autoGeneratedOldName = `${vals.join(', ')} Path`;
              const isAutoGeneratedName = (p.name ?? '').trim() === autoGeneratedOldName.trim();
              const newName = isAutoGeneratedName ? `${updatedVals.join(', ')} Path` : (p.name ?? '');
              return { ...p, trigger: newTrigger, name: newName };
            })
            .filter((p): p is SurveyPath => p != null);
        }

        pushHistory({ questions: next, paths: nextPaths });
        setPaths(nextPaths);
        return next;
      });
    },
    [paths, pushHistory]
  );

  /**
   * Delete a question. No-op for the done step (survey end); it cannot be removed.
   */
  const deleteQuestion = useCallback((id: string) => {
    const isDone = questions.some((q) => q.id === id && (q.type === 'done' || q.id === DONE_STEP_JSON_ID));
    if (isDone) return;
    const filteredQuestions = questions.filter((q) => q.id !== id);
    const nextPaths = paths.map((path) => ({
      ...path,
      questionIds: path.questionIds.filter((qId) => qId !== id),
    }));
    const filtered = renumberQuestions(filteredQuestions, nextPaths);
    pushHistory({ questions: filtered, paths: nextPaths });
    setQuestions(filtered);
    setPaths(nextPaths);
  }, [questions, paths, pushHistory]);

  /**
   * Reorder a question
   */
  const reorderQuestion = useCallback(
    (id: string, direction: 'up' | 'down') => {
      setQuestions((prev) => {
        const index = prev.findIndex((q) => q.id === id);
        if (index === -1) return prev;

        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= prev.length) return prev;

        const reordered = [...prev];
        [reordered[index], reordered[newIndex]] = [
          reordered[newIndex],
          reordered[index],
        ];

        const renumbered = renumberQuestions(reordered, paths);
        pushHistory({ questions: renumbered, paths });
        return renumbered;
      });
    },
    [paths, pushHistory]
  );

  /**
   * Reorder a main question block (main question + all path questions under it) up or down.
   * Branches stay below their parent; moving the parent moves the whole block.
   */
  const reorderMainQuestionBlock = useCallback(
    (mainQuestionId: string, direction: 'up' | 'down') => {
      setQuestions((prev) => {
        const mainQuestions = prev.filter((q) => !q.pathId);
        const mainIndex = mainQuestions.findIndex((q) => q.id === mainQuestionId);
        if (mainIndex === -1) return prev;
        const mainQuestion = mainQuestions[mainIndex];
        if (mainQuestion?.type === 'done' || mainQuestion?.id === DONE_STEP_JSON_ID) return prev;
        const newBlockIndex = direction === 'up' ? mainIndex - 1 : mainIndex + 1;
        if (newBlockIndex < 0 || newBlockIndex >= mainQuestions.length) return prev;

        const pathIdsByTrigger = new Map<string, string[]>();
        paths.forEach((p) => {
          const triggerId = p.trigger.questionId;
          if (!pathIdsByTrigger.has(triggerId)) pathIdsByTrigger.set(triggerId, []);
          pathIdsByTrigger.get(triggerId)!.push(p.id);
        });

        const getBlockQuestionIds = (mainId: string): string[] => {
          const pathIds = pathIdsByTrigger.get(mainId) ?? [];
          const pathQuestionIds = new Set(
            pathIds.flatMap((pid) => paths.find((p) => p.id === pid)?.questionIds ?? [])
          );
          return [mainId, ...pathQuestionIds];
        };

        const mainIdsInOrder = mainQuestions.map((q) => q.id);
        const blocks: CardQuestion[][] = mainIdsInOrder.map((mainId) => {
          const ids = getBlockQuestionIds(mainId);
          return prev.filter((q) => ids.includes(q.id));
        });

        const [removed] = blocks.splice(mainIndex, 1);
        blocks.splice(newBlockIndex, 0, removed);
        const doneBlockIndex = blocks.findIndex((block) => block.some((q) => q.type === 'done' || q.id === DONE_STEP_JSON_ID));
        if (doneBlockIndex !== -1 && doneBlockIndex !== blocks.length - 1) {
          const [doneBlock] = blocks.splice(doneBlockIndex, 1);
          blocks.push(doneBlock);
        }
        const reordered = renumberQuestions(blocks.flat(), paths);
        pushHistory({ questions: reordered, paths });
        return reordered;
      });
    },
    [paths, pushHistory]
  );

  /**
   * Move a main question block to a specific index (among main questions).
   * Used by drag-and-drop when dropping between blocks.
   */
  const moveMainQuestionBlockToIndex = useCallback(
    (mainQuestionId: string, targetMainIndex: number) => {
      setQuestions((prev) => {
        const mainQuestions = prev.filter((q) => !q.pathId);
        const currentIndex = mainQuestions.findIndex((q) => q.id === mainQuestionId);
        if (currentIndex === -1 || currentIndex === targetMainIndex) return prev;
        const mainQuestion = mainQuestions[currentIndex];
        if (mainQuestion?.type === 'done' || mainQuestion?.id === DONE_STEP_JSON_ID) return prev;
        const pathIdsByTrigger = new Map<string, string[]>();
        paths.forEach((p) => {
          const triggerId = p.trigger.questionId;
          if (!pathIdsByTrigger.has(triggerId)) pathIdsByTrigger.set(triggerId, []);
          pathIdsByTrigger.get(triggerId)!.push(p.id);
        });
        const getBlockQuestionIds = (mainId: string): string[] => {
          const pathIds = pathIdsByTrigger.get(mainId) ?? [];
          const pathQuestionIds = new Set(
            pathIds.flatMap((pid) => paths.find((p) => p.id === pid)?.questionIds ?? [])
          );
          return [mainId, ...pathQuestionIds];
        };
        const mainIdsInOrder = mainQuestions.map((q) => q.id);
        const blocks: CardQuestion[][] = mainIdsInOrder.map((mainId) =>
          prev.filter((q) => getBlockQuestionIds(mainId).includes(q.id))
        );
        const [removed] = blocks.splice(currentIndex, 1);
        blocks.splice(targetMainIndex, 0, removed);
        const doneBlockIndex = blocks.findIndex((block) => block.some((q) => q.type === 'done' || q.id === DONE_STEP_JSON_ID));
        if (doneBlockIndex !== -1 && doneBlockIndex !== blocks.length - 1) {
          const [doneBlock] = blocks.splice(doneBlockIndex, 1);
          blocks.push(doneBlock);
        }
        const reordered = renumberQuestions(blocks.flat(), paths);
        pushHistory({ questions: reordered, paths });
        return reordered;
      });
    },
    [paths, pushHistory]
  );

  /**
   * Move a question into a path (add to branch). Removes from previous path if any.
   */
  const moveQuestionToPath = useCallback(
    (questionId: string, pathId: string) => {
      const path = paths.find((p) => p.id === pathId);
      if (!path) return;
      const nextQuestions = questions.map((q) =>
        q.id === questionId ? { ...q, pathId } : q
      );
      const nextPaths = paths.map((p) => {
        if (p.id === pathId) {
          if (p.questionIds.includes(questionId)) return p;
          return { ...p, questionIds: [...p.questionIds, questionId] };
        }
        if (p.questionIds.includes(questionId)) {
          return { ...p, questionIds: p.questionIds.filter((id) => id !== questionId) };
        }
        return p;
      });
      const renumbered = renumberQuestions(nextQuestions, nextPaths);
      pushHistory({ questions: renumbered, paths: nextPaths });
      setQuestions(renumbered);
      setPaths(nextPaths);
    },
    [questions, paths, pushHistory]
  );

  /**
   * Move a question out of its path into the main flow at targetMainIndex.
   */
  const moveQuestionOutOfPath = useCallback(
    (questionId: string, targetMainIndex: number) => {
      const question = questions.find((q) => q.id === questionId);
      if (!question?.pathId) return;
      const pathId = question.pathId;
      const path = paths.find((p) => p.id === pathId);
      if (!path) return;

      const nextPaths = paths.map((p) =>
        p.id === pathId ? { ...p, questionIds: p.questionIds.filter((id) => id !== questionId) } : p
      );
      const nextQuestions = questions.map((q) =>
        q.id === questionId ? { ...q, pathId: undefined } : q
      );
      const mainQuestions = nextQuestions.filter((q) => !q.pathId);
      const fromIndex = mainQuestions.findIndex((q) => q.id === questionId);
      const toIndex = Math.min(Math.max(0, targetMainIndex), mainQuestions.length - 1);
      if (fromIndex === -1) {
        pushHistory({ questions: nextQuestions, paths: nextPaths });
        setQuestions(nextQuestions);
        setPaths(nextPaths);
        return;
      }
      if (fromIndex === toIndex) {
        const renumbered = renumberQuestions(nextQuestions, nextPaths);
        pushHistory({ questions: renumbered, paths: nextPaths });
        setQuestions(renumbered);
        setPaths(nextPaths);
        return;
      }
      const reorderedMain = [...mainQuestions];
      const [moved] = reorderedMain.splice(fromIndex, 1);
      reorderedMain.splice(toIndex, 0, moved);
      const pathQuestions = nextQuestions.filter((q) => q.pathId);
      const reordered = renumberQuestions([...reorderedMain, ...pathQuestions], nextPaths);
      pushHistory({ questions: reordered, paths: nextPaths });
      setQuestions(reordered);
      setPaths(nextPaths);
    },
    [questions, paths, pushHistory]
  );

  /**
   * Reorder a question within its path (up/down). No-op if not in a path.
   */
  const reorderQuestionInPath = useCallback(
    (questionId: string, direction: 'up' | 'down') => {
      const question = questions.find((q) => q.id === questionId);
      if (!question?.pathId) return;
      const path = paths.find((p) => p.id === question.pathId);
      if (!path) return;

      const ids = [...path.questionIds];
      const idx = ids.indexOf(questionId);
      if (idx === -1) return;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= ids.length) return;

      [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
      const nextPaths = paths.map((p) =>
        p.id === path.id ? { ...p, questionIds: ids } : p
      );
      const renumbered = renumberQuestions(questions, nextPaths);
      pushHistory({ questions: renumbered, paths: nextPaths });
      setQuestions(renumbered);
      setPaths(nextPaths);
    },
    [questions, paths, pushHistory]
  );

  /**
   * Move a question to a specific index within its path (for drag-drop reorder).
   */
  const moveQuestionWithinPath = useCallback(
    (questionId: string, targetPathIndex: number) => {
      const question = questions.find((q) => q.id === questionId);
      if (!question?.pathId) return;
      const path = paths.find((p) => p.id === question.pathId);
      if (!path) return;

      const ids = [...path.questionIds];
      const fromIdx = ids.indexOf(questionId);
      if (fromIdx === -1 || fromIdx === targetPathIndex) return;
      ids.splice(fromIdx, 1);
      ids.splice(targetPathIndex, 0, questionId);
      const nextPaths = paths.map((p) =>
        p.id === path.id ? { ...p, questionIds: ids } : p
      );
      const renumbered = renumberQuestions(questions, nextPaths);
      pushHistory({ questions: renumbered, paths: nextPaths });
      setQuestions(renumbered);
      setPaths(nextPaths);
    },
    [questions, paths, pushHistory]
  );

  /**
   * Move a question into a path at a specific index (from main or another path).
   */
  const moveQuestionToPathAt = useCallback(
    (questionId: string, pathId: string, targetPathIndex: number) => {
      const path = paths.find((p) => p.id === pathId);
      if (!path) return;

      const nextQuestions = questions.map((q) =>
        q.id === questionId ? { ...q, pathId } : q
      );
      const nextPaths = paths.map((p) => {
        if (p.id !== pathId) {
          if (p.questionIds.includes(questionId)) {
            return { ...p, questionIds: p.questionIds.filter((id) => id !== questionId) };
          }
          return p;
        }
        const ids = p.questionIds.filter((id) => id !== questionId);
        ids.splice(Math.min(targetPathIndex, ids.length), 0, questionId);
        return { ...p, questionIds: ids };
      });
      const renumbered = renumberQuestions(nextQuestions, nextPaths);
      pushHistory({ questions: renumbered, paths: nextPaths });
      setQuestions(renumbered);
      setPaths(nextPaths);
    },
    [questions, paths, pushHistory]
  );

  /**
   * Add a new path
   */
  const addPath = useCallback((path: Omit<SurveyPath, 'id'>): string => {
    const newId = generateId();
    const newPath: SurveyPath = {
      ...path,
      id: newId,
      trigger: {
        questionId: path.trigger.questionId,
        values: normalizeTriggerValues(path.trigger.values),
      },
    };

    setPaths((prev) => {
      const next = [...prev, newPath];
      pushHistory({ questions, paths: next });
      return next;
    });
    return newId;
  }, [questions, pushHistory]);

  /**
   * Update a path
   */
  const updatePath = useCallback((id: string, updates: Partial<SurveyPath>) => {
    setPaths((prev) => {
      const next = prev.map((p) => {
        if (p.id !== id) return p;
        const merged = { ...p, ...updates };
        const safeTrigger = p.trigger ?? { questionId: '', values: [], label: undefined };
        if (updates.trigger !== undefined) {
          const vals = normalizeTriggerValues(merged.trigger?.values ?? safeTrigger.values);
          merged.trigger = {
            questionId: merged.trigger?.questionId ?? safeTrigger.questionId,
            values: vals,
            label: merged.trigger?.label ?? safeTrigger.label ?? vals[0],
          };
          // Keep auto-generated path name in sync with trigger values (e.g. "Option 1, Option 2 Path" → "Option 1 Path" when one option removed)
          merged.name = vals.length > 0 ? `${vals.join(', ')} Path` : (merged.name ?? p.name ?? '');
        }
        return merged;
      });
      pushHistory({ questions, paths: next });
      return next;
    });
  }, [questions, pushHistory]);

  /**
   * Delete a path
   */
  const deletePath = useCallback((id: string) => {
    const nextPaths = paths.filter((p) => p.id !== id);
    const nextQuestions = questions.map((q) => (q.pathId === id ? { ...q, pathId: undefined } : q));
    pushHistory({ questions: nextQuestions, paths: nextPaths });
    setPaths(nextPaths);
    setQuestions(nextQuestions);
  }, [questions, paths, pushHistory]);

  /**
   * Get the current card survey (always includes a final "done" step for reward/completion marker).
   * Done step: { id: "done", number: <integer>, type: "done" } for survey-reader compatibility.
   * Question numbers are emitted as numeric; path triggers include optional label for validation.
   */
  const getCardSurvey = useCallback((): CardSurvey => {
    const realQuestions = questions.filter((q) => q.type !== 'done' && q.id !== DONE_QUESTION_ID);
    let maxNum = 0;
    for (const q of realQuestions) {
      const n = typeof q.number === 'number' ? q.number : parseFloat(String(q.number));
      if (!Number.isNaN(n) && n > maxNum) maxNum = n;
    }
    const doneNumber = Math.floor(maxNum) + 1;
    const toNumericNumber = (q: CardQuestion): number => {
      const n = typeof q.number === 'number' ? q.number : parseFloat(String(q.number));
      return Number.isNaN(n) ? 0 : n;
    };
    const questionsNormalized = realQuestions.map((q) => ({
      ...q,
      number: toNumericNumber(q),
    }));
    const doneStep = { id: DONE_STEP_JSON_ID, number: doneNumber, type: 'done' as const };
    const questionsWithDone = [...questionsNormalized, doneStep];
    const pathsWithLabel = paths.map((p) => ({
      ...p,
      trigger: {
        ...p.trigger,
        label: p.trigger.label ?? normalizeTriggerValues(p.trigger.values)[0] ?? undefined,
      },
    }));
    return {
      format: 'card-v1',
      questions: questionsWithDone as CardQuestion[],
      paths: pathsWithLabel,
    };
  }, [questions, paths]);

  /**
   * Load a card survey (resets history). Hydrates minimal "done" steps from API to full CardQuestion.
   */
  const loadCardSurvey = useCallback((survey: CardSurvey) => {
    const hydratedQuestions = hydrateQuestions(survey.questions);
    const normalizedPaths = (survey.paths || []).map((p) => normalizePath(p as Partial<SurveyPath> & { id: string }));
    const snapshot = copySnapshot({ questions: hydratedQuestions, paths: normalizedPaths });
    setQuestions(hydratedQuestions);
    setPaths(normalizedPaths);
    setHistoryState({ history: [snapshot], index: 0 });
  }, []);

  /**
   * Get question by ID
   */
  const getQuestionById = useCallback(
    (id: string) => questions.find((q) => q.id === id),
    [questions]
  );

  /**
   * Get path by ID
   */
  const getPathById = useCallback(
    (id: string) => paths.find((p) => p.id === id),
    [paths]
  );

  /**
   * Get questions by path ID
   */
  const getQuestionsByPath = useCallback(
    (pathId: string) => {
      const path = paths.find((p) => p.id === pathId);
      if (!path) return [];
      const byId = new Map(questions.map((q) => [q.id, q]));
      return path.questionIds.map((id) => byId.get(id)).filter((q): q is CardQuestion => q != null);
    },
    [questions, paths]
  );

  return {
    questions,
    paths,
    addQuestion,
    addQuestionToPath,
    duplicateQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestion,
    reorderMainQuestionBlock,
    moveMainQuestionBlockToIndex,
    moveQuestionToPath,
    moveQuestionOutOfPath,
    reorderQuestionInPath,
    moveQuestionWithinPath,
    moveQuestionToPathAt,
    addPath,
    updatePath,
    deletePath,
    getCardSurvey,
    loadCardSurvey,
    getQuestionById,
    getPathById,
    getQuestionsByPath,
    undo,
    redo,
    canUndo,
    canRedo,
  };
}

/**
 * Helper: Renumber questions with semantic numbering.
 * Main questions (no pathId): 1, 2, 3, ...
 * Path questions: parent.number + '.' + subIndex (e.g. 2.1, 2.2)
 */
function renumberQuestions(questions: CardQuestion[], paths: SurveyPath[]): CardQuestion[] {
  const mainQuestions = questions.filter((q) => !q.pathId);
  const mainNumberById = new Map<string, number>();
  mainQuestions.forEach((q, i) => mainNumberById.set(q.id, i + 1));

  const pathNumberByQuestionId = new Map<string, number | string>();
  paths.forEach((path) => {
    const parentNum = mainNumberById.get(path.trigger.questionId);
    if (parentNum == null) return;
    path.questionIds.forEach((qId, j) => {
      pathNumberByQuestionId.set(qId, parseFloat(`${parentNum}.${j + 1}`));
    });
  });

  return questions.map((q) => {
    const num = q.pathId
      ? pathNumberByQuestionId.get(q.id)
      : mainNumberById.get(q.id);
    return { ...q, number: num ?? 1 };
  });
}

